import React, { useEffect, useMemo, useState } from 'react';
import { Avatar, Flex, Tooltip } from 'antd';
import { faSlack } from '@fortawesome/free-brands-svg-icons';
import { faCircleExclamation, faPlus, faSearch, faUsers } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { debounce, sortBy } from 'lodash-es';
import { useQueryClient } from 'react-query';
import { useParams } from 'react-router-dom';

import { trackEvent } from 'jf/analytics/Analytics';
import { RosterClient, SlackEntity, SlackTargetingClient, TargetRespondent } from 'jf/api';
import { useDevExTheme } from 'jf/common/themes/DevExTheme';
import { DevExButton } from 'jf/components/DevExButton';
import { DevExEmpty } from 'jf/components/DevExEmpty';
import { DevExLoader } from 'jf/components/DevExLoader';
import { DevExSelect } from 'jf/components/DevExSelect';
import { useClientMutation, useClientQuery } from 'jf/utils/useClientQuery';

type EmptySlackRosterDisplayProps = {
  user: TargetRespondent;
  rosterUsers: TargetRespondent[];
  onAdd: () => void;
};
export const EmptySlackRosterDisplay: React.FC<EmptySlackRosterDisplayProps> = ({
  user,
  rosterUsers,
  onAdd,
}) => {
  const theme = useDevExTheme();
  const queryClient = useQueryClient();

  const [searchQuery, setSearchQuery] = useState('');
  const [slackUser, setSlackUser] = useState();
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState('');

  const { data: slackEntities, isFetching } = useClientQuery(
    SlackTargetingClient.getSlackEntities,
    { filter: debouncedSearchQuery },
    { enabled: debouncedSearchQuery.length >= 1 }
  );
  const { studyRef } = useParams<{ studyRef: string }>();

  const debounceFiredSearchQuery = useMemo(() => debounce(setDebouncedSearchQuery, 300), []);
  useEffect(() => {
    if (searchQuery) {
      debounceFiredSearchQuery(searchQuery);
    } else {
      debounceFiredSearchQuery.cancel();
      setDebouncedSearchQuery('');
    }
  }, [searchQuery]);

  const rosterEmail = rosterUsers?.map((user) => user.email);
  const slackUsers = slackEntities?.filter(
    (user) => (user.type = SlackEntity.type.USER) && user.email !== null
  );

  const { mutateAsync: UpdateStudyRoster } = useClientMutation(RosterClient.updateStudyTargets);

  const isSearching = searchQuery !== debouncedSearchQuery || isFetching;

  const slackEntityOptions = useMemo(() => {
    return sortBy(slackUsers, (entity) => entity.name.toLowerCase()).map((entity) => {
      const disabled = entity.email && rosterEmail?.includes(entity.email) ? true : false;
      return {
        value: entity.id,
        key: '@' + entity.name,
        label: (
          <Tooltip
            title={disabled ? 'This Slack user is already linked to a person in your roster' : ''}
          >
            <Flex
              gap={theme.variable.spacing.sm}
              align="center"
              style={{ opacity: disabled ? theme.variable.opacity.disabled : 1 }}
            >
              <Avatar
                shape={'square'}
                style={{ backgroundColor: theme.color.background.active }}
                size="large"
                src={entity.image ?? <FontAwesomeIcon icon={faUsers} />}
              />
              <Flex vertical>
                {entity.displayName}
                <div style={{ color: theme.color.text.secondary }}>
                  {' '}
                  {`${entity.type === SlackEntity.type.CHANNEL ? '#' : '@'}${entity.name}`}
                </div>
              </Flex>
            </Flex>
          </Tooltip>
        ),
        disabled: disabled,
      };
    });
  }, [slackEntities]);

  return (
    <Flex gap={theme.variable.spacing.md} align="center">
      <Tooltip
        title={
          `${user.name}` +
          ' does not have Slack linked to their profile. Link a Slack profile here. '
        }
      >
        <FontAwesomeIcon
          icon={faCircleExclamation}
          style={{
            color: theme.color.status.warning.text,
            paddingInlineStart: theme.variable.spacing.sm,
          }}
        />
      </Tooltip>
      <Flex vertical gap={theme.variable.spacing.xs}>
        <div style={{ fontSize: theme.variable.fontSize.md }}> {user.name} </div>
        <div style={{ color: theme.color.text.secondary, minWidth: 200, wordBreak: 'break-all' }}>
          {user.email}
        </div>
      </Flex>
      <DevExSelect
        showSearch
        filterOption={() => true} // leave filtering to the BE only
        options={!isSearching ? slackEntityOptions : undefined}
        onChange={(value) => {
          UpdateStudyRoster({
            studyRef: studyRef!,
            targetRef: user?.publicReferenceId,
            requestBody: {
              name: user.name,
              email: user.email,
              slack: {
                slackUserId: value,
              },
            },
          }).then(() => {
            trackEvent('survey-editor:slack-target-editor:link-user', {
              studyRef: studyRef,
              targetRef: user?.publicReferenceId,
              slackUserId: value,
            });

            queryClient.invalidateQueries('GET_STUDY_TARGETS');
            setSlackUser(value);
          });
          setSearchQuery('');
        }}
        optionLabelProp="key"
        style={{ height: 40, width: 200 }}
        loading={isSearching}
        suffixIcon={<FontAwesomeIcon icon={faSearch} />}
        placeholder="Search for Slack user"
        notFoundContent={
          isSearching ? (
            <DevExLoader tip="Searching Slack..." />
          ) : (
            <DevExEmpty
              icon={faSlack}
              label={
                searchQuery
                  ? `No slack targets found matching "${searchQuery}".`
                  : 'Search for @users, @groups, and #channels in your Slack instance.'
              }
            />
          )
        }
        onSearch={setSearchQuery}
      />
      <DevExButton
        icon={<FontAwesomeIcon icon={faPlus} />}
        disabled={!slackUser}
        onClick={() => onAdd()}
      >
        Add
      </DevExButton>
    </Flex>
  );
};
